import { Fragment, type ReactNode } from 'react'
import { navigeer } from './router'

/* ===========================================================================
 *  Markdown, veilig
 *
 *  Voor omschrijvingen, reacties en wat de AI terugschrijft. Het levert
 *  React-elementen op en nooit HTML-tekst: wat iemand in een omschrijving
 *  typt wordt nooit als HTML geïnterpreteerd, ook niet als het er als HTML
 *  uitziet. Dat is hier geen theorie -- de AI schrijft wat hij wil, en een
 *  omschrijving kan uit een GitHub-issue komen die iedereen kan openen.
 *
 *  Wat het kan: koppen, vet, cursief, code, codeblokken, lijsten, vinkjes,
 *  citaten, links (alleen http, https en mailto), een streep. En taaknummers:
 *  WEB-12 wordt een link naar die taak.
 *
 *  Wat het niet kan: tabellen, plaatjes, HTML. Wie dat nodig heeft, heeft
 *  een wiki nodig en geen taakomschrijving.
 * =========================================================================== */

interface Opties {
  /** Een vinkje omgezet: het nieuwe tekstblok. */
  opVink?: (regel: number, af: boolean) => void
}

export function Markdown({ tekst, opties, klein }: { tekst: string; opties?: Opties; klein?: boolean }) {
  return <div className={`md ${klein ? 'md-klein' : ''}`}>{blokken(tekst ?? '', opties ?? {})}</div>
}

function blokken(tekst: string, o: Opties): ReactNode[] {
  const regels = tekst.replace(/\r\n?/g, '\n').split('\n')
  const uit: ReactNode[] = []
  let i = 0
  let k = 0

  while (i < regels.length) {
    const r = regels[i]!

    /* --- codeblok --- */
    const hek = r.match(/^\s*```(\S*)/)
    if (hek) {
      const code: string[] = []
      i++
      while (i < regels.length && !/^\s*```/.test(regels[i]!)) code.push(regels[i++]!)
      i++
      uit.push(<pre key={k++} className="md-code"><code>{code.join('\n')}</code></pre>)
      continue
    }

    /* --- lege regel --- */
    if (!r.trim()) { i++; continue }

    /* --- kop --- */
    const kop = r.match(/^(#{1,4})\s+(.*)$/)
    if (kop) {
      const n = kop[1]!.length
      const inhoud = inline(kop[2]!, o)
      uit.push(n === 1 ? <h3 key={k++}>{inhoud}</h3> : n === 2 ? <h4 key={k++}>{inhoud}</h4> : <h5 key={k++}>{inhoud}</h5>)
      i++
      continue
    }

    /* --- streep --- */
    if (/^\s*([-*_])\s*\1\s*\1[\s\-*_]*$/.test(r)) { uit.push(<hr key={k++} />); i++; continue }

    /* --- citaat --- */
    if (/^\s*>/.test(r)) {
      const deel: string[] = []
      while (i < regels.length && /^\s*>/.test(regels[i]!)) deel.push(regels[i++]!.replace(/^\s*>\s?/, ''))
      uit.push(<blockquote key={k++}>{blokken(deel.join('\n'), o)}</blockquote>)
      continue
    }

    /* --- lijst --- */
    if (/^\s*([-*+]|\d+[.)])\s+/.test(r)) {
      const genummerd = /^\s*\d+[.)]/.test(r)
      const punten: ReactNode[] = []
      while (i < regels.length && /^\s*([-*+]|\d+[.)])\s+/.test(regels[i]!)) {
        const regelNr = i
        const inhoud = regels[i]!.replace(/^\s*([-*+]|\d+[.)])\s+/, '')
        const vink = inhoud.match(/^\[( |x|X)\]\s+(.*)$/)
        if (vink) {
          const af = vink[1] !== ' '
          punten.push(
            <li key={i} className="md-vink">
              <input
                type="checkbox"
                checked={af}
                disabled={!o.opVink}
                onChange={() => o.opVink?.(regelNr, !af)}
              />
              <span className={af ? 'af' : ''}>{inline(vink[2]!, o)}</span>
            </li>,
          )
        } else {
          punten.push(<li key={i}>{inline(inhoud, o)}</li>)
        }
        i++
      }
      uit.push(genummerd ? <ol key={k++}>{punten}</ol> : <ul key={k++}>{punten}</ul>)
      continue
    }

    /* --- alinea: tot de volgende lege regel of het volgende blok --- */
    const alinea: string[] = []
    while (
      i < regels.length && regels[i]!.trim()
      && !/^(#{1,4}\s|\s*```|\s*>|\s*([-*+]|\d+[.)])\s+)/.test(regels[i]!)
    ) alinea.push(regels[i++]!)
    uit.push(
      <p key={k++}>
        {alinea.map((a, n) => <Fragment key={n}>{n > 0 && <br />}{inline(a, o)}</Fragment>)}
      </p>,
    )
  }
  return uit
}

/* Volgorde telt: code eerst, want binnen code is een sterretje gewoon een
   sterretje. */
const PATROON = new RegExp([
  '(`[^`]+`)',                                   // code
  '(\\*\\*[^*]+\\*\\*|__[^_]+__)',              // vet
  '(\\*[^*\\s][^*]*\\*|_[^_\\s][^_]*_)',        // cursief
  '(\\[[^\\]]+\\]\\([^)\\s]+\\))',               // [tekst](url)
  '(https?:\\/\\/[^\\s<>()]+[^\\s<>().,;:!?\'"])', // los adres
  '(\\b[A-Z][A-Z0-9]{1,9}-\\d{1,6}\\b)',          // WEB-12
].join('|'), 'g')

function veiligeUrl(u: string): string | null {
  try {
    const url = new URL(u, location.origin)
    return ['http:', 'https:', 'mailto:'].includes(url.protocol) ? url.href : null
  } catch {
    return null
  }
}

function inline(tekst: string, o: Opties): ReactNode[] {
  const uit: ReactNode[] = []
  let laatste = 0
  let k = 0
  for (const m of tekst.matchAll(PATROON)) {
    const begin = m.index ?? 0
    if (begin > laatste) uit.push(tekst.slice(laatste, begin))
    const [heel, code, vet, cursief, link, los, sleutel] = m

    if (code) uit.push(<code key={k++}>{code.slice(1, -1)}</code>)
    else if (vet) uit.push(<strong key={k++}>{inline(vet.slice(2, -2), o)}</strong>)
    else if (cursief) uit.push(<em key={k++}>{inline(cursief.slice(1, -1), o)}</em>)
    else if (link) {
      const lm = link.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/)
      const href = lm ? veiligeUrl(lm[2]!) : null
      uit.push(href
        ? <a key={k++} href={href} target="_blank" rel="noopener noreferrer">{lm![1]}</a>
        : <Fragment key={k++}>{heel}</Fragment>)
    } else if (los) {
      const href = veiligeUrl(los)
      uit.push(href ? <a key={k++} href={href} target="_blank" rel="noopener noreferrer">{los}</a> : los)
    } else if (sleutel) {
      /* /taak/WEB-12: de route zoekt zelf op welke taak dat is. */
      uit.push(
        <a key={k++} href={`/taak/${sleutel}`} className="md-sleutel"
          onClick={(e) => { e.preventDefault(); navigeer(`/taak/${sleutel}`) }}>{sleutel}</a>,
      )
    }
    laatste = begin + heel.length
  }
  if (laatste < tekst.length) uit.push(tekst.slice(laatste))
  return uit
}

/** Een vinkje in een tekst omzetten: "- [ ]" wordt "- [x]" op die regel. */
export function zetVink(tekst: string, regel: number, af: boolean): string {
  const regels = tekst.replace(/\r\n?/g, '\n').split('\n')
  const r = regels[regel]
  if (r === undefined) return tekst
  regels[regel] = r.replace(/\[( |x|X)\]/, af ? '[x]' : '[ ]')
  return regels.join('\n')
}
